import { useState, useEffect, KeyboardEvent } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDatePicker } from "@/components/calendar-date-picker";
import { DateRange } from "react-day-picker";
import { Search, X, Filter, RefreshCw, Plus, SlidersHorizontal, Phone, Hash, User, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface CallFilters {
  ids?: string[];
  assistant_ids?: string[];
  phone_number_ids?: string[];
  phone_numbers?: string[];
  customer_phone_numbers?: string[];
  ended_reasons?: string[];
  type?: 'inboundPhoneCall' | 'outboundPhoneCall' | 'webCall' | '';
  dateRange: DateRange;
}

interface CallsFiltersProps {
  filters: CallFilters;
  onFiltersChange: (filters: CallFilters) => void;
  onRefresh: () => void;
  isLoading?: boolean;
  compact?: boolean;
}

export function CallsFilters({ filters, onFiltersChange, onRefresh, isLoading, compact = false }: CallsFiltersProps) {
  const [localFilters, setLocalFilters] = useState<CallFilters>(filters);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Temporary input states for Enter key handling
  const [tempInputs, setTempInputs] = useState({
    id: '',
    assistant_id: '',
    phone_number_id: '',
    phone_number: '',
    customer_phone_number: '',
    ended_reason: ''
  });

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (key: keyof CallFilters, value: any) => {
    // For select fields and date range, apply immediately
    if (key === 'type' || key === 'dateRange') {
      if (key === 'type' && value === 'all') {
        value = '';
      }
      const newFilters = { ...localFilters, [key]: value };
      setLocalFilters(newFilters);
      onFiltersChange(newFilters);
    }
  };

  const handleInputChange = (key: keyof typeof tempInputs, value: string) => {
    setTempInputs(prev => ({ ...prev, [key]: value }));
  };

  const addValue = (filterKey: keyof CallFilters, inputKey: keyof typeof tempInputs) => {
    const value = tempInputs[inputKey].trim();
    if (!value) return;

    const currentValues = (localFilters[filterKey] as string[]) || [];
    if (!currentValues.includes(value)) {
      const newFilters = {
        ...localFilters,
        [filterKey]: [...currentValues, value]
      };
      setLocalFilters(newFilters);
      onFiltersChange(newFilters);
    }
    
    // Clear the input
    setTempInputs(prev => ({ ...prev, [inputKey]: '' }));
  };

  const removeValue = (filterKey: keyof CallFilters, value: string) => {
    const currentValues = (localFilters[filterKey] as string[]) || [];
    const newFilters = {
      ...localFilters,
      [filterKey]: currentValues.filter(v => v !== value)
    };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleInputKeyPress = (filterKey: keyof CallFilters, inputKey: keyof typeof tempInputs, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addValue(filterKey, inputKey);
    }
  };

  const handleClearFilter = (key: keyof CallFilters) => {
    const newFilters = { ...localFilters };
    if (key === 'dateRange') {
      newFilters.dateRange = { from: undefined, to: undefined };
    } else if (key === 'type') {
      newFilters.type = '';  // Set to empty string for 'all'
    } else {
      newFilters[key] = [] as any;
    }
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearAllFilters = () => {
    const clearedFilters: CallFilters = {
      ids: [],
      assistant_ids: [],
      phone_number_ids: [],
      phone_numbers: [],
      customer_phone_numbers: [],
      ended_reasons: [],
      type: '',
      dateRange: { from: undefined, to: undefined }
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    setTempInputs({
      id: '',
      assistant_id: '',
      phone_number_id: '',
      phone_number: '',
      customer_phone_number: '',
      ended_reason: ''
    });
  };

  const activeFilterCount = [
    ...(localFilters.ids || []),
    ...(localFilters.assistant_ids || []),
    ...(localFilters.phone_number_ids || []),
    ...(localFilters.phone_numbers || []),
    ...(localFilters.customer_phone_numbers || []),
    ...(localFilters.ended_reasons || []),
  ].filter(Boolean).length;

  // If compact mode, only show the buttons (for SiteHeader)
  if (compact) {
    return (
      <>
        <Popover open={showAdvanced} onOpenChange={setShowAdvanced}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="relative gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              Advanced Filters
              {activeFilterCount > 0 && (
                <Badge 
                  variant="destructive" 
                  className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs"
                >
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
            <PopoverContent className="w-96" align="end">
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-3">
                  <div className="flex items-center gap-2">
                    <Filter className="h-5 w-5 text-muted-foreground" />
                    <h4 className="font-semibold text-base">Advanced Filters</h4>
                  </div>
                  <p className="text-xs text-muted-foreground">Press Enter to add</p>
                </div>
                
                {/* Call ID */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    Call IDs
                  </label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter call ID..."
                      value={tempInputs.id}
                      onChange={(e) => handleInputChange('id', e.target.value)}
                      onKeyPress={(e) => handleInputKeyPress('ids', 'id', e)}
                      className="h-9"
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => addValue('ids', 'id')}
                      disabled={!tempInputs.id.trim()}
                      className="h-9 w-9"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {localFilters.ids && localFilters.ids.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {localFilters.ids.map(id => (
                        <Badge key={id} variant="secondary" className="text-xs">
                          {id}
                          <X 
                            className="h-3 w-3 ml-1 cursor-pointer hover:opacity-70" 
                            onClick={() => removeValue('ids', id)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Assistant ID */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    Assistant IDs
                  </label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter assistant ID..."
                      value={tempInputs.assistant_id}
                      onChange={(e) => handleInputChange('assistant_id', e.target.value)}
                      onKeyPress={(e) => handleInputKeyPress('assistant_ids', 'assistant_id', e)}
                      className="h-9"
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => addValue('assistant_ids', 'assistant_id')}
                      disabled={!tempInputs.assistant_id.trim()}
                      className="h-9 w-9"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {localFilters.assistant_ids && localFilters.assistant_ids.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {localFilters.assistant_ids.map(id => (
                        <Badge key={id} variant="secondary" className="text-xs">
                          {id}
                          <X 
                            className="h-3 w-3 ml-1 cursor-pointer hover:opacity-70" 
                            onClick={() => removeValue('assistant_ids', id)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Phone Number ID */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    Phone Number IDs
                  </label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter phone number ID..."
                      value={tempInputs.phone_number_id}
                      onChange={(e) => handleInputChange('phone_number_id', e.target.value)}
                      onKeyPress={(e) => handleInputKeyPress('phone_number_ids', 'phone_number_id', e)}
                      className="h-9"
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => addValue('phone_number_ids', 'phone_number_id')}
                      disabled={!tempInputs.phone_number_id.trim()}
                      className="h-9 w-9"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {localFilters.phone_number_ids && localFilters.phone_number_ids.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {localFilters.phone_number_ids.map(id => (
                        <Badge key={id} variant="secondary" className="text-xs">
                          {id}
                          <X 
                            className="h-3 w-3 ml-1 cursor-pointer hover:opacity-70" 
                            onClick={() => removeValue('phone_number_ids', id)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Phone Number */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    Phone Numbers
                  </label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter phone number..."
                      value={tempInputs.phone_number}
                      onChange={(e) => handleInputChange('phone_number', e.target.value)}
                      onKeyPress={(e) => handleInputKeyPress('phone_numbers', 'phone_number', e)}
                      className="h-9"
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => addValue('phone_numbers', 'phone_number')}
                      disabled={!tempInputs.phone_number.trim()}
                      className="h-9 w-9"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {localFilters.phone_numbers && localFilters.phone_numbers.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {localFilters.phone_numbers.map(number => (
                        <Badge key={number} variant="secondary" className="text-xs">
                          {number}
                          <X 
                            className="h-3 w-3 ml-1 cursor-pointer hover:opacity-70" 
                            onClick={() => removeValue('phone_numbers', number)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Customer Phone Number */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    Customer Phone Numbers
                  </label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter customer phone number..."
                      value={tempInputs.customer_phone_number}
                      onChange={(e) => handleInputChange('customer_phone_number', e.target.value)}
                      onKeyPress={(e) => handleInputKeyPress('customer_phone_numbers', 'customer_phone_number', e)}
                      className="h-9"
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => addValue('customer_phone_numbers', 'customer_phone_number')}
                      disabled={!tempInputs.customer_phone_number.trim()}
                      className="h-9 w-9"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {localFilters.customer_phone_numbers && localFilters.customer_phone_numbers.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {localFilters.customer_phone_numbers.map(number => (
                        <Badge key={number} variant="secondary" className="text-xs">
                          {number}
                          <X 
                            className="h-3 w-3 ml-1 cursor-pointer hover:opacity-70" 
                            onClick={() => removeValue('customer_phone_numbers', number)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* End Reason */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-muted-foreground" />
                    End Reasons
                  </label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter end reason..."
                      value={tempInputs.ended_reason}
                      onChange={(e) => handleInputChange('ended_reason', e.target.value)}
                      onKeyPress={(e) => handleInputKeyPress('ended_reasons', 'ended_reason', e)}
                      className="h-9"
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => addValue('ended_reasons', 'ended_reason')}
                      disabled={!tempInputs.ended_reason.trim()}
                      className="h-9 w-9"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {localFilters.ended_reasons && localFilters.ended_reasons.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {localFilters.ended_reasons.map(reason => (
                        <Badge key={reason} variant="secondary" className="text-xs">
                          {reason}
                          <X 
                            className="h-3 w-3 ml-1 cursor-pointer hover:opacity-70" 
                            onClick={() => removeValue('ended_reasons', reason)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </PopoverContent>
        </Popover>

        {/* Clear All Filters */}
        {activeFilterCount > 0 && (
          <Button 
            variant="outline" 
            onClick={handleClearAllFilters}
            size="sm"
            className="gap-2 text-destructive hover:text-destructive"
          >
            <X className="h-4 w-4" />
            Clear All Filters
          </Button>
        )}
      </>
    );
  }

  // Full mode (below content area) - only show active filter badges
  return (
    <div className="space-y-4">
      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {localFilters.ids && localFilters.ids.map(id => (
            <Badge key={id} variant="secondary" className="gap-1">
              Call ID: {id}
              <X 
                className="h-3 w-3 cursor-pointer hover:opacity-70" 
                onClick={() => removeValue('ids', id)}
              />
            </Badge>
          ))}
          {localFilters.type && (
            <Badge variant="secondary" className="gap-1">
              Type: {localFilters.type === 'inboundPhoneCall' ? 'Inbound' : localFilters.type === 'outboundPhoneCall' ? 'Outbound' : 'Web Call'}
              <X 
                className="h-3 w-3 cursor-pointer hover:opacity-70" 
                onClick={() => handleClearFilter('type')}
              />
            </Badge>
          )}
          {localFilters.assistant_ids && localFilters.assistant_ids.map(id => (
            <Badge key={id} variant="secondary" className="gap-1">
              Assistant: {id}
              <X 
                className="h-3 w-3 cursor-pointer hover:opacity-70" 
                onClick={() => removeValue('assistant_ids', id)}
              />
            </Badge>
          ))}
          {localFilters.phone_number_ids && localFilters.phone_number_ids.map(id => (
            <Badge key={id} variant="secondary" className="gap-1">
              Phone ID: {id}
              <X 
                className="h-3 w-3 cursor-pointer hover:opacity-70" 
                onClick={() => removeValue('phone_number_ids', id)}
              />
            </Badge>
          ))}
          {localFilters.phone_numbers && localFilters.phone_numbers.map(number => (
            <Badge key={number} variant="secondary" className="gap-1">
              Phone: {number}
              <X 
                className="h-3 w-3 cursor-pointer hover:opacity-70" 
                onClick={() => removeValue('phone_numbers', number)}
              />
            </Badge>
          ))}
          {localFilters.customer_phone_numbers && localFilters.customer_phone_numbers.map(number => (
            <Badge key={number} variant="secondary" className="gap-1">
              Customer: {number}
              <X 
                className="h-3 w-3 cursor-pointer hover:opacity-70" 
                onClick={() => removeValue('customer_phone_numbers', number)}
              />
            </Badge>
          ))}
          {localFilters.ended_reasons && localFilters.ended_reasons.map(reason => (
            <Badge key={reason} variant="secondary" className="gap-1">
              Reason: {reason}
              <X 
                className="h-3 w-3 cursor-pointer hover:opacity-70" 
                onClick={() => removeValue('ended_reasons', reason)}
              />
            </Badge>
          ))}
          {(localFilters.dateRange.from || localFilters.dateRange.to) && (
            <Badge variant="secondary" className="gap-1">
              Date: {localFilters.dateRange.from?.toLocaleDateString()} - {localFilters.dateRange.to?.toLocaleDateString()}
              <X 
                className="h-3 w-3 cursor-pointer hover:opacity-70" 
                onClick={() => handleClearFilter('dateRange')}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
} 